/** The sidebar draws before this module's chunk loads, so its labels are registered up front (see messages.nav.ts). */
export const reportsNavEn = {
  "reports.nav.dashboard": "Dashboard",
  "reports.nav.library": "Report library",
  "reports.nav.builder": "Report builder",
} as const;

export const reportsEn = {
  ...reportsNavEn,

  "reports.back.library": "Report library",
  "reports.contact.followup": "Follow-up",
  "reports.contact.review": "Review members",
  "reports.contact.reviewCohort": "Review members: {cohort}",

  // ---- dashboard
  "reports.dashboard.title": "Reports dashboard",
  "reports.dashboard.subtitle": "Key figures, trends and top lists from the club's own data.",
  "reports.dashboard.asOf": "Figures as of {date}. Each key figure is compared with the same days last year.",
  "reports.dashboard.tiles": "Key figures",
  "reports.dashboard.noAccess": "No access. Your role cannot see the data behind this.",
  "reports.dashboard.noData": "Nothing to show for this period yet.",
  "reports.dashboard.tileError": "This figure could not be worked out.",
  "reports.dashboard.panelError": "This section could not be loaded.",
  "reports.dashboard.nothingTitle": "No reports data is open to your role",
  "reports.dashboard.nothingBody": "The dashboard uses member, billing and tee sheet data. Ask a club administrator to give your role access to the data you need.",
  "reports.dashboard.viewReport": "View report",
  "reports.dashboard.openTile": "Open the report behind {label}",
  "reports.dashboard.openPanel": "Open the report behind {title}",

  "reports.tile.up": "Up {percent}",
  "reports.tile.down": "Down {percent}",
  "reports.tile.same": "No change",
  "reports.tile.vs": "against {before} on {date}",
  "reports.tile.noBase": "Nothing to compare with: {before} on {date}",
  "reports.tile.secondary.active_members_one": "{formatted} household",
  "reports.tile.secondary.active_members_other": "{formatted} households",
  "reports.tile.secondary.rounds_mtd_one": "{formatted} guest round",
  "reports.tile.secondary.rounds_mtd_other": "{formatted} guest rounds",
  "reports.tile.secondary.ar_over_60_one": "{formatted} account",
  "reports.tile.secondary.ar_over_60_other": "{formatted} accounts",

  // ---- library
  "reports.library.title": "Report library",
  "reports.library.subtitle": "Canned reports for the questions clubs ask most, and the reports your colleagues saved and shared.",
  "reports.library.newReport": "New report",
  "reports.library.canned": "Canned reports",
  "reports.library.noCanned": "There are no canned reports.",
  "reports.library.needsAccess": "Needs access to {dataset} data, which your role does not have.",
  "reports.library.saved": "Saved reports",
  "reports.library.savedCaption": "Saved reports",
  "reports.library.search": "Search saved reports",
  "reports.library.mine": "Only my reports",
  "reports.library.noSaved": "No saved reports yet",
  "reports.library.noSavedBody": "Run a canned report and save it, or build your own in the report builder.",
  "reports.library.noMatch": "No saved report matches",
  "reports.library.noMatchBody": "Try other words, or show everyone's reports.",
  "reports.library.col.name": "Report",
  "reports.library.col.owner": "Owner",
  "reports.library.col.dataset": "Data",
  "reports.library.col.sharedWith": "Shared with",
  "reports.library.col.updated": "Updated",
  "reports.library.col.actions": "Actions",
  "reports.library.notShared": "Not shared",
  "reports.library.you": "You",
  "reports.library.unknownOwner": "Unknown",
  "reports.library.unavailable": "Not available to you",
  "reports.library.open": "Open",
  "reports.library.openNamed": "Open {name}",
  "reports.library.duplicateNamed": "Duplicate {name}",
  "reports.library.shareNamed": "Share {name}",
  "reports.library.schedulesNamed": "Schedules for {name}",
  "reports.library.deleteNamed": "Delete {name}",

  // ---- a canned report
  "reports.canned.title": "Canned report",
  "reports.canned.missingTitle": "That report does not exist",
  "reports.canned.missingBody": "It may have been removed. Choose another from the library.",
  "reports.canned.deniedTitle": "You cannot run this report",
  "reports.canned.deniedBody": "It reads {dataset} data, which your role does not have access to.",
  "reports.canned.save": "Save as report",
  "reports.canned.customize": "Customize in builder",
  "reports.canned.saveTitle": "Save as report",
  "reports.canned.saveButton": "Save report",
  "reports.params.label": "Report parameters",
  "reports.params.run": "Run report",
  "reports.params.any": "Any",
  "reports.param.problem.required": "This field is required.",
  "reports.param.problem.date": "Enter a date such as 2026-10-14.",
  "reports.param.problem.integer": "Enter a whole number.",
  "reports.param.problem.range": "That number is outside the allowed range.",
  "reports.param.problem.choice": "Choose one of the listed values.",
  "reports.param.problem.order": "The start date must not be after the end date.",

  // ---- a saved report
  "reports.saved.title": "Saved report",
  "reports.saved.owner": "Owner",
  "reports.saved.dataset": "Data",
  "reports.saved.updated": "Updated",
  "reports.saved.sharedWith": "Shared with",
  "reports.saved.duplicate": "Duplicate",
  "reports.saved.edit": "Edit in builder",
  "reports.saved.share": "Share",
  "reports.saved.schedules": "Schedules",
  "reports.saved.created": "Saved {name}.",
  "reports.saved.duplicated": "Created {name}.",
  "reports.saved.readOnly": "Only {owner} can change this report. Duplicate it to get a copy of your own.",
  "reports.saved.notRunnableTitle": "You cannot run this report",
  "reports.saved.notRunnableBody": "It reads data your role cannot see, or the canned report behind it no longer exists.",
  "reports.delete.title": "Delete this report?",
  "reports.delete.body": "{name} and its schedules will be deleted for everyone it is shared with. This cannot be undone.",
  "reports.delete.done": "Deleted {name}.",

  "reports.save.name": "Name",
  "reports.save.description": "Description",
  "reports.share.title": "Share report",
  "reports.share.body": "People whose role is chosen here can find and run this report. They still only see data their role may see.",
  "reports.share.roles": "Share with these roles",
  "reports.share.hint": "Only you can change or delete the report.",
  "reports.share.noRoles": "The club has no roles to share with.",
  "reports.share.saved": "Sharing updated.",

  // ---- schedules
  "reports.schedule.title": "Schedules for {name}",
  "reports.schedule.notLiveTitle": "Email delivery is not switched on yet.",
  "reports.schedule.notLive": "Schedules are saved now, but nothing is emailed until the club's notification service is turned on. Until then every schedule shows Not delivered.",
  "reports.schedule.none": "This report has no schedules.",
  "reports.schedule.add": "Add a schedule",
  "reports.schedule.edit": "Change this schedule",
  "reports.schedule.addButton": "Add schedule",
  "reports.schedule.cadence": "Repeats",
  "reports.schedule.cadence.daily": "Every day",
  "reports.schedule.cadence.weekly": "Every week",
  "reports.schedule.cadence.monthly": "Every month",
  "reports.schedule.weekday": "Day of the week",
  "reports.schedule.dayOfMonth": "Day of the month",
  "reports.schedule.dayHint": "1 to 28, so it happens in every month.",
  "reports.schedule.hour": "Time",
  "reports.schedule.hourHint": "In the club's time zone.",
  "reports.schedule.recipients": "Email addresses",
  "reports.schedule.recipientsHint": "Separate addresses with commas or line breaks. At most 25.",
  "reports.schedule.recipientsRequired": "Add at least one email address.",
  "reports.schedule.recipientsTooMany": "Use at most 25 addresses.",
  "reports.schedule.recipientsInvalid": "{address} is not an email address.",
  "reports.schedule.active": "Active",
  "reports.schedule.daily": "Every day at {time}",
  "reports.schedule.weekly": "Every {weekday} at {time}",
  "reports.schedule.monthly": "On day {day} of every month at {time}",
  "reports.schedule.to": "To {recipients}",
  "reports.schedule.next": "Next run {when}",
  "reports.schedule.notDelivered": "Not delivered",
  "reports.schedule.waiting": "Waiting for its first run",
  "reports.schedule.paused": "Paused",
  "reports.schedule.pause": "Pause",
  "reports.schedule.resume": "Resume",
  "reports.schedule.deleteTitle": "Delete this schedule?",
  "reports.schedule.deleteBody": "The schedule {rhythm} will be removed. The report itself stays.",

  // ---- result
  "reports.result.rows_one": "{formatted} row",
  "reports.result.rows_other": "{formatted} rows",
  "reports.result.showingMore": "Showing the first {count} rows. More are available.",
  "reports.result.elapsed": "ran in {time}",
  "reports.result.truncated": "This report was cut after {count} rows. Narrow it with a filter or a shorter period to see everything.",
  "reports.result.emptyNoHistoryTitle": "No data yet",
  "reports.notices.label": "What the data covers",
  "reports.result.emptyTitle": "No rows",
  "reports.result.emptyBody": "Nothing matches these settings. Widen the dates or remove a filter.",
  "reports.result.caption": "{title}: report rows",
  "reports.export.button": "Export CSV",
  "reports.export.done": "The CSV file was downloaded.",
  "reports.export.doneTruncated": "The CSV file was downloaded, but the report was too long and the file was cut. Narrow the report to get everything.",
  "reports.print.button": "Print",
  "reports.print.generated": "{dataset} data, generated {when}",
  "reports.weekOf": "Week of {date}",

  // ---- charts
  "reports.chart.showTable": "Show as table",
  "reports.chart.showChart": "Show chart",
  "reports.chart.tableCaption": "{title}: the values behind the chart",
  "reports.chart.description": "{type} of {title}: {categories} categories and {series} series. Choose Show as table to read the values.",
  "reports.chart.other": "Other",
  "reports.chart.partial": "The chart draws the {count} rows loaded so far.",
  "reports.chart.skipped_one": "{count} category with no positive amount is left out of the chart.",
  "reports.chart.skipped_other": "{count} categories with no positive amount are left out of the chart.",
  "reports.chartType.table": "Table",
  "reports.chartType.bar": "Bar chart",
  "reports.chartType.line": "Line chart",
  "reports.chartType.pie": "Pie chart",
  "reports.chartType.donut": "Donut chart",
  "reports.chartType.area": "Area chart",
  "reports.chartType.kpi": "Single number",
  "reports.chartKind.table": "Table",
  "reports.chartKind.bar": "Bar chart, upright",
  "reports.chartKind.barSideways": "Bar chart, sideways",
  "reports.chartKind.stacked": "Stacked bar chart",
  "reports.chartKind.line": "Line chart",
  "reports.chartKind.area": "Area chart",
  "reports.chartKind.pie": "Pie chart",
  "reports.chartKind.donut": "Donut chart",
  "reports.chartKind.kpi": "Single number",
  "reports.chart.summaryAxis": "Highest: {top} ({topValue}). Lowest: {bottom} ({bottomValue}).",
  "reports.chart.summaryOne": "Only one value: {top} ({topValue}).",
  "reports.chart.summaryShare": "Largest part: {top}, {topValue} ({percent}% of the whole). Smallest: {bottom}, {bottomValue}.",
  "reports.chart.summaryNone": "Nothing to draw: the result has no values.",
  "reports.chart.kpiValue": "{label}: {value}",

  // ---- builder
  "reports.builder.title": "Report builder",
  "reports.builder.subtitle": "Choose the data, shape it, and watch the result. Save it to run again, share it, or schedule it.",
  "reports.builder.editing": "Editing a saved report.",
  "reports.builder.dataset": "1. Data",
  "reports.builder.datasetHint": "What do you want to report on?",
  "reports.builder.datasetLabel": "Dataset",
  "reports.builder.chooseDataset": "Choose a dataset",
  "reports.builder.shape": "2. Shape",
  "reports.mode.detail": "List rows",
  "reports.mode.detailHint": "One line for each record, with the columns you choose.",
  "reports.mode.summary": "Summarize",
  "reports.mode.summaryHint": "Group the records and add them up: totals, counts, averages.",
  "reports.builder.columns": "3. Columns",
  "reports.builder.columnsHint": "The columns of the report, in order.",
  "reports.builder.columnsChosen": "Columns in this report",
  "reports.builder.addColumn": "Add a column",
  "reports.builder.chooseField": "Choose a field",
  "reports.builder.dragHandle": "Reorder {name}: drag it, or press the up or down arrow key",
  "reports.builder.moveUp": "Move {name} up",
  "reports.builder.moveDown": "Move {name} down",
  "reports.builder.removeNamed": "Remove {name}",
  "reports.builder.groupBy": "3. Group by",
  "reports.builder.groupByHint": "One row for each different value. Group a date by month, quarter or year to see a trend.",
  "reports.builder.groupNumber": "Group {number}",
  "reports.builder.addGroup": "Add a grouping",
  "reports.builder.removeGroup": "Remove grouping {number}",
  "reports.builder.field": "Field",
  "reports.builder.bucket": "Group dates by",
  "reports.builder.label": "Column name",
  "reports.builder.measures": "4. Measures",
  "reports.builder.measuresHint": "What to work out for each group: a total, a count, an average.",
  "reports.builder.measureNumber": "Measure {number}",
  "reports.builder.addMeasure": "Add a measure",
  "reports.builder.removeMeasure": "Remove measure {number}",
  "reports.builder.measureField": "Of",
  "reports.builder.countRows": "Number of rows",
  "reports.builder.aggregate": "Work out the",
  "reports.builder.filters": "Filters",
  "reports.builder.filtersHint": "Only include records that match every filter.",
  "reports.builder.filterNumber": "Filter {number}",
  "reports.builder.addFilter": "Add a filter",
  "reports.builder.removeFilter": "Remove filter {number}",
  "reports.builder.operator": "Condition",
  "reports.builder.params": "Data options",
  "reports.builder.paramsHint": "Settings of this dataset. Leave blank to use the default.",
  "reports.builder.paramDefault": "Default: {value}",
  "reports.builder.having": "Keep only groups where",
  "reports.builder.havingHint": "Drop groups whose total is too small or too large, for example spend above zero.",
  "reports.builder.havingNumber": "Condition {number}",
  "reports.builder.havingMeasure": "Measure",
  "reports.builder.addHaving": "Add a condition",
  "reports.builder.removeHaving": "Remove condition {number}",
  "reports.builder.sort": "Sort",
  "reports.builder.sortHint": "The order of the rows. The first sort comes first.",
  "reports.builder.sortNumber": "Sort {number}",
  "reports.builder.sortBy": "Sort by",
  "reports.builder.direction": "Direction",
  "reports.builder.asc": "Lowest first",
  "reports.builder.desc": "Highest first",
  "reports.builder.addSort": "Add a sort",
  "reports.builder.removeSort": "Remove sort {number}",
  "reports.builder.limit": "Top results",
  "reports.builder.limitHint": "Keep only the first rows after sorting, for example the top 10.",
  "reports.builder.topN": "Show only the first",
  "reports.builder.topNHint": "Rows, 1 to {max}. Leave blank to show all.",
  "reports.builder.within": "Ranking",
  "reports.builder.withinHint": "To get the top 5 of each department, choose Department.",
  "reports.builder.withinNone": "In the whole report",
  "reports.builder.withinEach": "Within each {name}",
  "reports.builder.chart": "Chart",
  "reports.builder.chartHint": "Choose how to draw the summary. The table is always shown too, and you can switch any chart to its table.",
  "reports.builder.chartType": "Chart type",
  "reports.builder.chartAuto": "Choose the axis and series for me",
  "reports.builder.chartX": "Across the chart",
  "reports.builder.chartSeries": "Separate lines or bars by",
  "reports.builder.chartNoSeries": "Nothing",
  "reports.builder.chartY": "Value",
  "reports.builder.chartYs": "Values",
  "reports.builder.chartStackedAreas": "Stack the areas",
  "reports.builder.preview": "Preview",
  "reports.builder.previewFailed": "The preview could not be run",
  "reports.builder.startTitle": "Start with a dataset",
  "reports.builder.startBody": "Choose the data you want to report on, and the result appears here as you build.",
  "reports.builder.finish": "Finish setting up the report to see it:",
  "reports.builder.finishFirst": "Finish setting up the report first.",
  "reports.builder.unsaved": "Unsaved changes",
  "reports.builder.save": "Save",
  "reports.builder.saveButton": "Save report",
  "reports.builder.saveTitle": "Save report",
  "reports.builder.saveAsNew": "Save as new",
  "reports.builder.saveAsNewTitle": "Save as a new report",
  "reports.builder.copyName": "Copy of {name}",
  "reports.builder.details": "Name and sharing",
  "reports.builder.detailsTitle": "Name and sharing",
  "reports.builder.saved": "Saved {name}.",
  "reports.builder.changeDatasetTitle": "Change the dataset?",
  "reports.builder.changeDatasetBody": "The fields of one dataset do not exist in another, so the report starts over.",
  "reports.builder.changeDataset": "Change dataset",
  "reports.builder.cannedTitle": "This is a canned report",
  "reports.builder.cannedBody": "A canned report keeps its own definition. Open it and choose Customize in builder to make a copy you can change.",
  "reports.builder.openReport": "Open the report",
  "reports.builder.notYoursTitle": "Only the owner can change this report",
  "reports.builder.notYoursBody": "{owner} saved it. Duplicate it from its page to get a copy of your own.",
  "reports.builder.noDatasetTitle": "You cannot open this report in the builder",
  "reports.builder.noDatasetBody": "It uses data your role does not have access to.",
  "reports.bucket.exact": "Exact date",
  "reports.words.count": "Number of rows",
  "reports.words.bucketed": "{field} by {bucket}",

  "reports.filter.value": "Value",
  "reports.filter.values": "Values",
  "reports.filter.from": "From",
  "reports.filter.to": "To",
  "reports.filter.range": "Date range",
  "reports.filter.chooseRange": "Choose a date range",
  "reports.filter.choose": "Choose",
  "reports.filter.listHint": "Separate values with commas.",
  "reports.filter.moneyHint": "An amount such as {example}.",

  "reports.builder.groupNone": "No grouping yet, so the report is one total row. Add a grouping, for example Department, to get a row for each.",
  "reports.builder.measureNone": "Add a measure: what to total or count for each group.",
  "reports.builder.previewRejected": "The report was not accepted: {detail}",
  "reports.problem.chartNeedsSummary": "A chart draws a summary. Switch the shape to Summarize first.",
  "reports.problem.chartNeedsAxis": "This chart needs at least one grouping (across the chart) and one measure (the values).",
  "reports.problem.chartShare": "A pie or donut shows the parts of one whole: use exactly one grouping and one measure.",
  "reports.problem.chartOrdered": "A line or area chart needs an ordered axis. Group by a date (for example by month) or by a number.",
  "reports.problem.chartStack": "Stacking needs more than one measure, or a second grouping to split each bar by.",
  "reports.problem.chartSingle": "A single number needs exactly one measure and no grouping.",
  "reports.problem.datasetRequired": "Choose a dataset.",
  "reports.problem.columnsRequired": "Add at least one column.",
  "reports.problem.summaryRequired": "Add a grouping or a measure.",
  "reports.problem.fieldRequired": "Choose a field.",
  "reports.problem.unknownField": "That field is not in this dataset.",
  "reports.problem.notGroupable": "This field cannot be used to group a report.",
  "reports.problem.notFilterable": "This field cannot be filtered.",
  "reports.problem.bucketNotDate": "Only dates can be grouped by month, quarter or year.",
  "reports.problem.duplicate": "This is already in the report.",
  "reports.problem.aggNotAllowed": "That calculation is not available for this field.",
  "reports.problem.opNotAllowed": "That condition does not fit this field.",
  "reports.problem.keyRequired": "Choose a measure.",
  "reports.problem.unknownColumn": "That column is not in the report.",
  "reports.problem.tooManySorts": "Use at most 4 sorts.",
  "reports.problem.limit": "Enter a whole number from 1 to 1000.",
  "reports.problem.rankNeedsLimit": "Enter how many rows to keep first.",
  "reports.problem.rankUnknown": "Choose one of the groupings.",
  "reports.problem.number": "Enter a number.",
  "reports.problem.paramRequired": "This option is required.",
  "reports.problem.paramDate": "Enter a date such as 2026-10-14.",
  "reports.problem.paramInteger": "Enter a whole number.",
  "reports.problem.required": "Enter a value.",
  "reports.problem.integer": "Enter a whole number.",
  "reports.problem.money": "Enter an amount such as 25.00.",
  "reports.problem.date": "Enter a date such as 2026-10-14.",
  "reports.problem.order": "The first value must not be above the second.",
  "reports.problem.list": "Use at most 200 values.",
  "reports.problem.boolean": "Choose yes or no.",

  "reports.op.eq": "is",
  "reports.op.ne": "is not",
  "reports.op.in": "is one of",
  "reports.op.not_in": "is none of",
  "reports.op.gt": "is greater than",
  "reports.op.gte": "is at least",
  "reports.op.lt": "is less than",
  "reports.op.lte": "is at most",
  "reports.op.between": "is between",
  "reports.op.contains": "contains",
  "reports.op.starts_with": "starts with",
  "reports.op.is_null": "is empty",
  "reports.op.is_not_null": "is not empty",
  "reports.op.preset": "is in",
  "reports.opDate.eq": "is on",
  "reports.opDate.ne": "is not on",
  "reports.opDate.gt": "is after",
  "reports.opDate.gte": "is on or after",
  "reports.opDate.lt": "is before",
  "reports.opDate.lte": "is on or before",

  "reports.preset.today": "Today",
  "reports.preset.yesterday": "Yesterday",
  "reports.preset.this_week": "This week",
  "reports.preset.last_week": "Last week",
  "reports.preset.this_month": "This month",
  "reports.preset.last_month": "Last month",
  "reports.preset.this_quarter": "This quarter",
  "reports.preset.last_quarter": "Last quarter",
  "reports.preset.this_year": "This year",
  "reports.preset.last_year": "Last year",
  "reports.preset.last_7_days": "Last 7 days",
  "reports.preset.last_30_days": "Last 30 days",
  "reports.preset.last_90_days": "Last 90 days",
  "reports.preset.last_12_months": "Last 12 months",
  "reports.preset.year_to_date": "Year to date",
  "reports.preset.month_to_date_last_year": "Month to date, last year",

  "reports.agg.sum": "Total",
  "reports.agg.count": "Count",
  "reports.agg.avg": "Average",
  "reports.agg.min": "Lowest",
  "reports.agg.max": "Highest",
  "reports.agg.count_distinct": "Count of different values",
  "reports.agg.sum.of": "Total {field}",
  "reports.agg.count.of": "Count of {field}",
  "reports.agg.avg.of": "Average {field}",
  "reports.agg.min.of": "Lowest {field}",
  "reports.agg.max.of": "Highest {field}",
  "reports.agg.count_distinct.of": "Different {field}",
  "reports.bucket.day": "Day",
  "reports.bucket.week": "Week",
  "reports.bucket.month": "Month",
  "reports.bucket.quarter": "Quarter",
  "reports.bucket.year": "Year",

  // ---- the server's own errors for reports, where a fixed text reads better than its message
  "apiError.report_not_found": "That report no longer exists, or it is not shared with you.",
  "apiError.report_not_yours": "Only the person who saved a report can change or delete it. Duplicate it to get your own copy.",
  "apiError.schedule_not_found": "That schedule no longer exists.",
  "apiError.report_template_not_found": "The canned report behind this one no longer exists.",
  "apiError.report_timeout": "This report took too long and was stopped. Narrow the dates, add a filter, or group by less detail.",
  "apiError.mixed_report": "A report either lists rows or summarizes them, not both. Remove the columns or switch the shape.",
  "apiError.columns_required": "Choose at least one column, or add a grouping or a measure.",
  "apiError.summary_only": "Conditions on totals and ranking within a group only apply to a summary.",
  "apiError.cannot_group": "That field cannot be used to group a report.",
  "apiError.invalid_measure": "That calculation is not available for this field.",
  "apiError.invalid_bucket": "Only dates can be grouped by day, week, month, quarter or year.",
  "apiError.duplicate_column": "The same grouping or measure is in the report twice.",
  "apiError.unknown_column": "A sort or a condition on totals uses a column that is not in the report.",
  "apiError.invalid_rank": "Ranking within a group needs one of the report's groupings and a top-N number.",
  "apiError.invalid_value": "A value in this report is too large, too small or not valid for the data.",
  "apiError.invalid_filter_op": "That condition cannot be used with this field.",
  "apiError.invalid_chart": "The chart uses a column that is not in the report.",
  "apiError.chart_needs_summary": "A chart draws a summary. Switch the shape to Summarize first.",
  "apiError.chart_needs_axis": "This chart needs at least one grouping (across the chart) and one measure (the values).",
  "apiError.chart_share": "A pie or donut shows the parts of one whole: use exactly one grouping and one measure.",
  "apiError.chart_ordered_axis": "A line or area chart needs an ordered axis. Group by a date (for example by month) or by a number.",
  "apiError.chart_stack_needs_series": "Stacking needs more than one measure, or a second grouping to split each bar by.",
  "apiError.chart_single_number": "A single number needs exactly one measure and no grouping.",
  "apiError.chart_horizontal_bar_only": "Only bar charts can be laid out sideways.",
} as const;
export type ReportsMessages = typeof reportsEn;
